import { useQuery } from '@apollo/client';
import { GET_PRICING_INFO } from 'graphql/queries/getPricingInfo';
import { useEffect, useMemo } from 'react';
import { useParkingStore } from 'stores/parkingStore';
import { getParkzoneState, useParkzoneStore } from 'stores/parkzoneStore';
import {
  CountryCode,
  Maybe,
  PredefinedTicket,
  Query,
  QueryPricingInfoArgs,
  SegmentProps,
  SegmentPropsNotes,
} from 'types/generatedSchemaTypes';
import { getLang } from 'utils/commonUtils';
import useGetAvailableCustomerSegments from './useGetAvailableCustomerSegments';

/**
 * Type representing the return type of the useParkingPricingInfo hook
 */
export type ParkingPricingInfoResult = {
  /** Time from which parking is allowed */
  parkingAllowedFrom: string;
  /** Time until which parking is allowed */
  parkingAllowedUntil: string;
  /** Country code of the parking zone */
  countryCode: CountryCode;
  /** Notes related to the parking zone (only for normal parking type) */
  notes?: Maybe<Maybe<SegmentPropsNotes>[]>;
  /** Loading state for pricing info query */
  pricingInfoLoading: boolean;
  /** Error state for pricing info query */
  pricingInfoError: any;
  /** List of predefined tickets for the selected segment */
  predefinedTickets: Maybe<PredefinedTicket>[];
  /** Available pricing segments for the user */
  pricingSegments: SegmentProps[];
  /** Whether parking is allowed in the selected segment */
  parkingAllowed: boolean;
  /** ID of the pricing scheme */
  pricingSchemeId?: Maybe<string>;
  /** Whether to show default chips */
  showDefaultChips?: Maybe<boolean>;
  /** Whether only predefined tickets are available */
  predefinedTicketsOnly?: Maybe<boolean>;
  /** Whether the pricing engine is legacy */
  isLegacy: boolean;
  /** Country code for pricing */
  pricingCountryCode?: Maybe<CountryCode>;
  /** Whether the parking zone has predefined tickets */
  hasPredefinedTickets: boolean;
  /** All segments, including those not available to the user */
  allSegments: SegmentProps[];
  /** Whether all pricing segments are private and the user has no access */
  isPrivateOnlyWithNoAccess: boolean;
};

/**
 * Hook to fetch and manage parking pricing information for a specific parking zone
 *
 * @param parkingZoneUid - Unique identifier of the parking zone
 * @param cacheFirst - Whether to use cache-first fetch policy (defaults to false)
 * @param isAnpr - Whether the parking zone uses ANPR (Automatic Number Plate Recognition) (defaults to false)
 * @returns Parking pricing information and related states
 */
const useParkingPricingInfo = (
  parkingZoneUid: string,
  cacheFirst = false,
  isAnpr = false
): ParkingPricingInfoResult => {
  const lang = getLang();
  const pricingTypes = getParkzoneState().parkingPricingInfo;
  const { selectedParkingSegment, setSelectedParkingSegment } = useParkingStore(
    (state) => state
  );

  const {
    data: pricingInfoData,
    loading: pricingInfoLoading,
    error: pricingInfoError,
  } = useQuery<Query, QueryPricingInfoArgs>(GET_PRICING_INFO, {
    variables: { parkingZoneUid, lang },
    fetchPolicy: cacheFirst ? 'cache-first' : 'cache-and-network',
    skip: !parkingZoneUid || isAnpr,
  });

  const activeSegmentId = useParkzoneStore((state) => state.selectedTariffId);

  const pricingMemo = useMemo(
    () => [pricingInfoData?.pricingInfo],
    [pricingInfoData?.pricingInfo]
  );

  const {
    availableSegments: pricingSegments,
    allSegments,
    isPrivateOnlyWithNoAccess,
  } = useGetAvailableCustomerSegments(pricingMemo);

  useEffect(() => {
    if (activeSegmentId && pricingSegments) {
      const selectedItem = pricingSegments?.find(
        (item) => item?.uid === activeSegmentId
      );
      if (!selectedItem) {
        const defaultSegment = pricingSegments?.find(
          (item) => item?.uid === 'Standard'
        );
        setSelectedParkingSegment(defaultSegment);
        return;
      }
      setSelectedParkingSegment(selectedItem);
    } else {
      setSelectedParkingSegment(undefined);
    }
  }, [activeSegmentId, pricingSegments, setSelectedParkingSegment]);

  const predefinedTicketsMemoized = useMemo(() => {
    return selectedParkingSegment?.predefinedTickets || [];
  }, [selectedParkingSegment?.predefinedTickets]);

  const hasPredefinedTickets = useMemo(
    () =>
      (pricingSegments
        ?.flatMap((segment) => segment?.predefinedTickets)
        ?.filter(
          (predefinedTicket) => predefinedTicket?.sessionType === 'PREPAID'
        )?.length ?? 0) > 0,
    [pricingSegments]
  );

  const segmentParkingAllowed =
    selectedParkingSegment?.parkingAllowed ??
    pricingInfoData?.pricingInfo?.defaultSegment?.parkingAllowed;

  const isLegacy =
    pricingInfoData?.pricingInfo?.pricingEngine === 'LEGACY_TICKETAPI';

  return {
    parkingAllowedFrom: selectedParkingSegment?.parkingAllowedFrom || '',
    parkingAllowedUntil: selectedParkingSegment?.parkingAllowedUntil || '',
    countryCode: pricingInfoData?.pricingInfo?.countryCode as CountryCode,
    notes:
      pricingTypes?.parkingType !== 'normal'
        ? undefined
        : selectedParkingSegment?.notes,
    pricingInfoLoading,
    pricingInfoError,
    predefinedTickets: predefinedTicketsMemoized,
    pricingSegments,
    parkingAllowed: Boolean(segmentParkingAllowed && !isLegacy),
    pricingSchemeId: pricingInfoData?.pricingInfo?.pricingSchemeId,
    showDefaultChips: selectedParkingSegment?.showDefaultChips,
    predefinedTicketsOnly: selectedParkingSegment?.predefinedTicketsOnly,
    isLegacy,
    pricingCountryCode: pricingInfoData?.pricingInfo?.countryCode,
    hasPredefinedTickets,
    allSegments,
    isPrivateOnlyWithNoAccess,
  };
};

export default useParkingPricingInfo;
